import { connect, useSelector } from 'react-redux'
import { omit, uniqueId } from 'lodash'
import Immutable from 'immutable'
import PropTypes from 'prop-types'
import React from 'react'
import cc from 'classcat'

import { GotoContext } from '../GotoProvider'
import { QuantityContext } from './Cart/CartContext'
import { addToCart, setBuyboxBusyState, setBuyboxNotification, setBuyboxProductQuantity } from '../../store/actions'
import { generateCartUrl } from '../../urlGenerators'
import { sendAddToCart } from '../../utils/googleAnalytics'
import { track } from '../../utils/tracking'
import Link from './Link'
import QuantityField from './QuantityField'
import VariationSelection from './VariationSelection'
import compose from '../../utils/compose'
import translate from '../../utils/translate'
import withI18n from '../../components/withI18n'

export class BuyboxRaw extends React.Component {
  static propTypes = {
    children: PropTypes.element.isRequired,
    product: PropTypes.object.isRequired,
    addToCart: PropTypes.func.isRequired,
    setBuyboxBusyState: PropTypes.func.isRequired,
    setNotification: PropTypes.func.isRequired,
    setQuantity: PropTypes.func.isRequired,
    isEcommerceDisabled: PropTypes.bool.isRequired,
    t: PropTypes.func.isRequired,
    tReady: PropTypes.bool,
    quantity: PropTypes.number,
    redirectToCart: PropTypes.bool,
  }

  static defaultProps = {
    quantity: 1,
    redirectToCart: false,
  }

  static contextType = GotoContext

  get isProductAvailable() {
    return this.props.product.available
  }

  getQuantity = () => this.props.quantity

  setQuantity = (quantity) => Promise.resolve(this.props.setQuantity(quantity))

  addToCart = async () => {
    const { product, t, quantity } = this.props
    const cartUrl = generateCartUrl()

    this.props.setBuyboxBusyState(true)

    return this.props
      .addToCart(product.productId, quantity)
      .then((cart) => {
        const notification = {
          id: uniqueId(`${product.productId}-success-`),
          message: (
            <span>
              {t('notifications.productAddedToCart', {
                count: quantity,
              })}{' '}
              <Link className="add-to-cart-notice-message-link" to={cartUrl}>
                {t('views.storefrontView.goToCartLink.label')}
              </Link>
            </span>
          ),
          type: 'success',
        }

        track('cart:add', { cart, product, quantity })
        this.props.setNotification(notification)
      })
      .catch((err) => {
        const stockLevelError = /Low StockLevel/.test(err.toString())
        const error = stockLevelError
          ? t(`notifications.productAddedToCartError${quantity > 1 ? '_plural' : ''}`)
          : err.toString()
        const notification = {
          id: uniqueId(`${product.productId}-failure-`),
          message: error,
          type: 'failure',
        }

        this.props.setNotification(notification)
      })
      .finally(() => {
        this.setQuantity(1)
        this.props.setBuyboxBusyState(false)

        sendAddToCart(product, quantity)
        if (this.props.redirectToCart) return this.context.goto(generateCartUrl())
      })
  }

  render() {
    const { isEcommerceDisabled, product, children } = this.props
    if (isEcommerceDisabled) {
      // The variation selection is part of the Buybox, but should still be
      // shown when e-commerce is disabled.
      if (product.variations?.items) {
        return (
          <div className="variations-attribute">
            <VariationSelection product={product} key={product.slug} />
          </div>
        )
      }

      return null
    }
    return <QuantityContext.Provider value={this}>{children}</QuantityContext.Provider>
  }
}

export const AddToCartButton = ({ className, dispatch, ...other }) => {
  const isBusy = useSelector((state) => state.getIn(['view', 'buybox', 'isBusy'], false))
  const buybox = React.useContext(QuantityContext)

  return (
    <button
      {...other}
      className={cc([className, { pending: isBusy }])}
      disabled={isBusy || !buybox.isProductAvailable}
      onClick={() => buybox.addToCart()}
      type="button"
      title={
        buybox.props.product.hasVariations
          ? buybox.props.t('components.productComponent.addToBasketButton.selectVariationTooltip')
          : null
      }
    />
  )
}

AddToCartButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  dispatch: PropTypes.func,
}

// https://github.com/oliviertassinari/babel-plugin-transform-react-remove-prop-types/blob/v0.4.15/README.md#is-it-safe
export const AddToCartNoticeRawPropTypes = {
  t: PropTypes.func.isRequired,
  tReady: PropTypes.bool,
  className: PropTypes.string,
  dispatch: PropTypes.func,
  duration: PropTypes.number,
  i18n: PropTypes.object,
  notification: PropTypes.object,
}

class AddToCartNoticeRaw extends React.Component {
  static propTypes = AddToCartNoticeRawPropTypes

  static defaultProps = {
    duration: 8000,
    notification: {},
  }

  state = {
    display: false,
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.notification.id !== this.props.notification.id) {
      this.setState({ display: true })

      // if there's a timer running while a new notification is displayed then
      // prevent this old timer from hiding the new notification (too soon / instantly)
      this.timer && window.clearTimeout(this.timer)

      // start new cancelable timer
      this.timer = window.setTimeout(() => this.closeNotice(), this.props.duration)
    }
  }

  componentWillUnmount() {
    if (this.timer) window.clearTimeout(this.timer)
  }

  closeNotice = () => {
    this.setState({ display: false })
  }

  render() {
    const { message, type } = this.props.notification

    const { className, t } = this.props
    const htmlAttributes = omit(this.props, Object.keys(AddToCartNoticeRawPropTypes))
    const visibility = this.state.display ? 'visible' : 'hidden'

    return (
      <div
        {...htmlAttributes}
        className={cc([
          className,
          'add-to-cart-notice',
          `add-to-cart-notice-${visibility}`,
          {
            [`add-to-cart-notice-${type}`]: type,
          },
        ])}
      >
        <div className="add-to-cart-notice-wrapper">
          {message && <span className="add-to-cart-notice-message">{message}</span>}
          <span
            className="add-to-cart-notice-close"
            onClick={this.closeNotice}
            title={t('notifications.dismissButton.tooltip')}
          />
        </div>
      </div>
    )
  }
}

const Buybox = compose(
  withI18n('shop'),
  translate(),
  connect(
    (state) => ({
      quantity: state.getIn(['view', 'buybox', 'productQuantity']),
      isEcommerceDisabled: Boolean(
        state
          .getIn(['shop', 'attributes'])
          .find((attribute) => attribute.get('name') === 'ecommerce:disabled' && attribute.get('value') === 'true'),
      ),
    }),
    (dispatch) => ({
      addToCart: (productId, quantity) => dispatch(addToCart(productId, quantity, { showErrorNotification: false })),
      setBuyboxBusyState: (isBusy) => dispatch(setBuyboxBusyState(isBusy)),
      setNotification: (notification) => dispatch(setBuyboxNotification(notification)),
      setQuantity: (quantity) => dispatch(setBuyboxProductQuantity(quantity)),
    }),
  ),
)(BuyboxRaw)

export const BuyboxQuantityField = connect((state) => ({
  quantity: state.getIn(['view', 'buybox', 'productQuantity']),
}))(QuantityField)

export const AddToCartNotice = compose(
  withI18n('shop'),
  translate(),
  connect((state) => ({
    notification: state.getIn(['view', 'buybox', 'notification'], new Immutable.Map()).toJS(),
  })),
)(AddToCartNoticeRaw)

export default Buybox
